import { useMutation, useQuery } from '@tanstack/react-query';

import {
  EmraPasswordResetRequestSchema,
  EmraSignupRequestSchema,
  InviteAcceptRequestSchema,
  InviteAcceptResponseSchema,
  LoginRequestSchema,
  LoginResponseSchema,
  MfaEnrollmentCancelRequestSchema,
  MfaEnrollmentCompleteRequestSchema,
  MfaEnrollmentCompleteResponseSchema,
  MfaRecoveryCodeChallengeRequestSchema,
  MfaRecoveryCodeChallengeResponseSchema,
  MfaTotpChallengeRequestSchema,
  MfaTotpChallengeResponseSchema,
  MfaTotpEnrollResponseSchema,
  MyAbilitiesResponseSchema,
  PasswordResetConfirmSchema,
  RefreshTokenResponseSchema,
  SignupResponseSchema,
  TokenPairSchema,
  VerifyEmailRequestSchema,
  VerifyEmailResponseSchema,
  type EmraPasswordResetRequestDto,
  type EmraSignupRequestDto,
  type InviteAcceptRequestDto,
  type InviteAcceptResponseDto,
  type JwtAccessClaimsDto,
  type LoginRequestDto,
  type LoginResponseDto,
  type MfaEnrollmentCancelRequestDto,
  type MfaEnrollmentCompleteRequestDto,
  type MfaEnrollmentCompleteResponseDto,
  type MfaRecoveryCodeChallengeRequestDto,
  type MfaRecoveryCodeChallengeResponseDto,
  type MfaTotpChallengeRequestDto,
  type MfaTotpChallengeResponseDto,
  type MfaTotpEnrollResponseDto,
  type MyAbilitiesResponseDto,
  type PasswordResetConfirmDto,
  type RefreshTokenResponseDto,
  type SignupResponseDto,
  type VerifyEmailRequestDto,
  type VerifyEmailResponseDto,
} from '@org/contracts';

import { useAuth } from './AuthProvider';

import { apiClient } from '@/api/client';

// Auth mutation hooks. Per ADR-0005 §3 every TanStack Query hook lives in
// `features/<name>/api.ts`; auth lives one level higher because it is
// cross-feature. Response bodies are parsed through the matching contract
// schema so the union narrows correctly and stale fields surface in dev.
//
// EMRA uses the multi-tenant LoginRequestSchema (carries `tenantSlug`); EMR
// uses EmrLoginRequestSchema (.strict() — single-tenant per ADR-0004 §1).

export function useLogin() {
  return useMutation<LoginResponseDto, unknown, LoginRequestDto>({
    mutationFn: async (body) => {
      const validated = LoginRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/login', validated);
      return LoginResponseSchema.parse(response.data);
    },
  });
}

export function useVerifyMfaTotp() {
  return useMutation<MfaTotpChallengeResponseDto, unknown, MfaTotpChallengeRequestDto>({
    mutationFn: async (body) => {
      const validated = MfaTotpChallengeRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/mfa/verify-totp', validated);
      return MfaTotpChallengeResponseSchema.parse(response.data);
    },
  });
}

export function useVerifyMfaRecovery() {
  return useMutation<
    MfaRecoveryCodeChallengeResponseDto,
    unknown,
    MfaRecoveryCodeChallengeRequestDto
  >({
    mutationFn: async (body) => {
      const validated = MfaRecoveryCodeChallengeRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/mfa/verify-recovery-code', validated);
      return MfaRecoveryCodeChallengeResponseSchema.parse(response.data);
    },
  });
}

/**
 * Wrapper around the silent-refresh flow. Production code never calls this;
 * the response interceptor in `src/api/client.ts` handles the refresh path
 * automatically. Exposed for tests that need to assert the wire shape.
 */
export function useRefresh() {
  return useMutation<RefreshTokenResponseDto, unknown, void>({
    mutationFn: async () => {
      const response = await apiClient.post('/v1/auth/refresh', {});
      return RefreshTokenResponseSchema.parse(response.data);
    },
  });
}

export function useLogout() {
  const { logout } = useAuth();
  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      await logout();
    },
  });
}

// HCP-106 auth-tail mutation hooks. Each mirrors the corresponding endpoint
// on `apps/emra/api/src/modules/identity/identity.controller.ts`. EMRA-shape
// (multi-tenant) variants carry `tenantSlug` on the wire — see
// `EmraSignupRequestSchema` / `EmraPasswordResetRequestSchema`. Token-only
// endpoints (verify-email, password-reset confirm, invite accept) reuse the
// unprefixed schemas because the token carries the tenant claim.

export function useSignup() {
  return useMutation<SignupResponseDto, unknown, EmraSignupRequestDto>({
    mutationFn: async (body) => {
      const validated = EmraSignupRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/signup', validated);
      return SignupResponseSchema.parse(response.data);
    },
  });
}

export function useVerifyEmail() {
  return useMutation<VerifyEmailResponseDto, unknown, VerifyEmailRequestDto>({
    mutationFn: async (body) => {
      const validated = VerifyEmailRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/verify-email', validated);
      return VerifyEmailResponseSchema.parse(response.data);
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation<void, unknown, EmraPasswordResetRequestDto>({
    mutationFn: async (body) => {
      const validated = EmraPasswordResetRequestSchema.parse(body);
      await apiClient.post('/v1/auth/password/reset-request', validated);
    },
  });
}

export function useConfirmPasswordReset() {
  return useMutation<void, unknown, PasswordResetConfirmDto>({
    mutationFn: async (body) => {
      const validated = PasswordResetConfirmSchema.parse(body);
      await apiClient.post('/v1/auth/password/reset-confirm', validated);
    },
  });
}

export function useAcceptInvite() {
  return useMutation<InviteAcceptResponseDto, unknown, InviteAcceptRequestDto>({
    mutationFn: async (body) => {
      const validated = InviteAcceptRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/invite/accept', validated);
      return InviteAcceptResponseSchema.parse(response.data);
    },
  });
}

// HCP-108 forced-MFA-enrollment mutation hooks (ADR-0007). These three
// endpoints are presented to a user who has NO access token yet — the login
// response was the third union branch (`mfaEnrollmentRequired: true,
// enrollmentToken`). The token rides the `X-Enrollment-Token` header
// per-request. Each call sets `skipAuth`, so the axios request interceptor in
// `src/api/client.ts` never adds a Bearer header. An access token left from
// an earlier session belongs to another user, and the api refuses an
// enrollment token sent with a Bearer token.
//
// EMRA-specific tenant-scoping invariant: the pending enrollment-token's
// `tid` claim is verified server-side on every call (ADR-0007 §EMRA). A
// pending token issued for tenant A presented to tenant B's enroll endpoint
// is rejected at pending-token-verify before any DB write. The SPA does
// not pass a tenant identifier on the wire — tenant context rides the JWT
// claim, exactly like the existing voluntary MFA flow (HCP-107).

export function useStartTotpEnrollmentWithToken() {
  return useMutation<MfaTotpEnrollResponseDto, unknown, { enrollmentToken: string }>({
    mutationFn: async ({ enrollmentToken }) => {
      const response = await apiClient.post(
        '/v1/mfa/totp/enroll',
        {},
        {
          headers: { 'X-Enrollment-Token': enrollmentToken },
          skipAuth: true,
        },
      );
      return MfaTotpEnrollResponseSchema.parse(response.data);
    },
  });
}

export function useMfaEnrollmentComplete() {
  return useMutation<MfaEnrollmentCompleteResponseDto, unknown, MfaEnrollmentCompleteRequestDto>({
    mutationFn: async (body) => {
      const validated = MfaEnrollmentCompleteRequestSchema.parse(body);
      const response = await apiClient.post('/v1/auth/mfa-enrollment-complete', validated, {
        headers: { 'X-Enrollment-Token': validated.enrollmentToken },
        skipAuth: true,
      });
      return MfaEnrollmentCompleteResponseSchema.parse(response.data);
    },
  });
}

export function useMfaEnrollmentCancel() {
  return useMutation<void, unknown, MfaEnrollmentCancelRequestDto>({
    mutationFn: async (body) => {
      const validated = MfaEnrollmentCancelRequestSchema.parse(body);
      await apiClient.post('/v1/auth/mfa-enrollment-cancel', validated, {
        skipAuth: true,
      });
    },
  });
}

// Ability mirror (ADR-0009, ADR-0023 §6). The caller's effective
// CASL rules, keyed by user and token issued-at so a new access token
// refetches them. They live only in the in-memory TanStack cache, which
// AuthProvider clears when auth is cleared. Never in web storage or the URL.
export const authKeys = {
  all: ['auth'] as const,
  abilities: (sub: string | undefined, iat: number | undefined) =>
    [...authKeys.all, 'abilities', sub, iat] as const,
};

const ABILITIES_STALE_TIME_MS = 5 * 60_000;

export function useMyAbilitiesQuery(claims: JwtAccessClaimsDto | null) {
  const sub = claims?.sub;
  return useQuery<MyAbilitiesResponseDto>({
    queryKey: authKeys.abilities(sub, claims?.iat),
    queryFn: async () => {
      const response = await apiClient.get('/v1/auth/me/abilities');
      return MyAbilitiesResponseSchema.parse(response.data);
    },
    enabled: claims !== null,
    staleTime: ABILITIES_STALE_TIME_MS,
    retry: 1,
    // Same user, new token: keep the previous rules while the new ones load,
    // so the nav doesn't blink. A different user never inherits them.
    placeholderData: (previousData, previousQuery) => {
      if (sub !== undefined && previousQuery?.queryKey[2] === sub) {
        return previousData;
      }
      return undefined;
    },
  });
}

// Re-export the token-pair schema for callers that need to type-check a
// successful login (the non-MFA branch of LoginResponseSchema).
export { TokenPairSchema };
